import React from 'react';
import { getTheme, useThemeMode } from '../theme';
import { ThemeSwitch } from './ThemeSwitch';
import { QButton } from '../q/QButton';
import { huntBarSurface } from '../karta/MapUIComponents';
import { navSlots, splitNavItems } from '../nav/navState';
import { UnreadBadge } from './chat/ChatList';

/** Bottenmenyns höjd (den flytande ovalen) utan marginal och säkerhetsmarginal. */
export const BOTTOM_NAV_H = 60;
/** Avståndet från ovalen till skärmens nederkant (ovanför iPhones hemindikator). */
const BOTTOM_GAP = 10;
/** Utrymme sidorna lämnar längst ned så att inget hamnar under menyn. */
export const BOTTOM_NAV_SPACE = `calc(${BOTTOM_NAV_H + BOTTOM_GAP * 2}px + env(safe-area-inset-bottom, 0px))`;

export type BottomKey = 'q' | 'karta' | 'chatt' | 'minajakt';
/** Var användaren är, för att markera rätt punkt (Mer när sidan ligger under Mer). Inställningar nås via kugghjulet i toppraden. */
export type BottomActive = BottomKey | 'jakt' | 'profil' | 'admin' | 'installningar' | null;

const P = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
const ICONS: Record<Exclude<BottomKey, 'q'> | 'viltkott' | 'mer' | 'jakt' | 'profil' | 'admin' | 'erbjud' | 'logout', React.ReactNode> = {
  karta: <><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z" /><path d="M9 4v14M15 6v14" /></>,
  chatt: <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1.1-4.4A8 8 0 1 1 21 12z" />,
  viltkott: <><path d="M15.5 4.5c2.6 1.3 4 4.4 3 7.5-1.2 3.6-5.2 6.6-9.4 6.9-2.7.2-4.6-1.3-4.6-3.6 0-2.6 2.4-3.6 3.6-5.9 1.3-2.5 4.4-6.2 7.4-4.9z" /><circle cx="14.5" cy="9.5" r="1.6" /></>,
  mer: <><circle cx="5" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="19" cy="12" r="1.4" /></>,
  minajakt: <><path d="M3 11 12 4l9 7" /><path d="M5 10v10h14V10" /><path d="M10 20v-6h4v6" /></>,
  jakt: <><circle cx="11" cy="11" r="7" /><path d="m21 21-5-5" /></>,
  profil: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  admin: <><path d="M12 3 4 6v6c0 4.5 3.4 8.3 8 9 4.6-.7 8-4.5 8-9V6z" /></>,
  erbjud: <><path d="M12 5v14M5 12h14" /></>,
  logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5M21 12H9" /></>,
};
const Icon = ({ k, size = 22 }: { k: keyof typeof ICONS; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...P} aria-hidden="true">{ICONS[k]}</svg>
);

const LABELS: Record<BottomKey, string> = { q: 'Assistent', karta: 'Karta', chatt: 'Chatt', minajakt: 'Min jakt' };
const ORDER: BottomKey[] = ['q', 'karta', 'chatt', 'minajakt'];
/** Sidor som ligger under Mer (då markeras Mer). */
const UNDER_MORE: BottomActive[] = ['jakt', 'profil', 'admin'];

/**
 * Bottenmeny för iOS-appen och smal webb (samma flytande, frostade oval som jaktmenyn, orange linje
 * på vald punkt). Assistent (Q), Karta, Chatt och Min jakt; det som inte får plats, plus Mer jakt,
 * Viltkött och kontot, ligger under "Mer" (som i Qdomus). Inställningar nås via kugghjulet i toppraden
 * (UX1). Göms när kartan är i helskärm.
 */
export function BottomNav({
  active, onSelect, onQ, qOpen, onMerJakt, onViltkott, onProfil, onAdmin, onErbjud, onLogout, badges,
}: {
  active: BottomActive;
  /** Röd bubbla med antal (CH1: olästa chattmeddelanden på Chatt). */
  badges?: Partial<Record<BottomKey, number>>;
  onSelect: (k: Exclude<BottomKey, 'q'>) => void;
  /** Q: ett tryck öppnar panelen, ett långt tryck börjar lyssna direkt. */
  onQ: (listen: boolean) => void;
  qOpen?: boolean;
  onMerJakt: () => void;
  onViltkott: () => void;
  onProfil: () => void;
  /** Bara för admin. */
  onAdmin?: () => void;
  onErbjud: () => void;
  onLogout: () => void;
}) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [open, setOpen] = React.useState(false);
  const [width, setWidth] = React.useState(() => (typeof window === 'undefined' ? 375 : window.innerWidth));

  React.useEffect(() => {
    const r = () => setWidth(window.innerWidth);
    window.addEventListener('resize', r);
    return () => window.removeEventListener('resize', r);
  }, []);
  React.useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open]);

  // Ovalen är 12 px in från kanterna och högst 520 px bred; 6 px inre marginal på var sida.
  const barW = Math.min(width - 24, 520) - 12;
  const { primary, more } = splitNavItems(ORDER, navSlots(barW));
  const inMore = (k: BottomActive) => UNDER_MORE.includes(k) || (more as BottomActive[]).includes(k);
  const merActive = open || (!qOpen && inMore(active));

  const pick = (k: BottomKey) => {
    setOpen(false);
    if (k !== 'q') onSelect(k);
  };
  const run = (f: () => void) => () => { setOpen(false); f(); };

  const item = (on: boolean): React.CSSProperties => ({
    flex: '1 1 0', minWidth: 0, height: BOTTOM_NAV_H - 12, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
    borderRadius: 999, border: `1.5px solid ${on ? T.amber : 'transparent'}`,
    background: on ? `color-mix(in srgb, ${T.amber} 14%, transparent)` : 'transparent',
    cursor: 'pointer', padding: '0 2px', fontFamily: 'inherit', boxSizing: 'border-box',
    color: on ? T.amber : T.text, fontSize: 10.5, fontWeight: on ? 700 : 600, letterSpacing: '0.01em',
    transition: 'background 0.15s, border-color 0.15s, color 0.15s', WebkitTapHighlightColor: 'transparent',
  });
  const labelStyle: React.CSSProperties = { maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  const row = (on = false): React.CSSProperties => ({
    width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px', minHeight: 44, background: 'transparent',
    border: 'none', cursor: 'pointer', color: on ? T.amber : T.text, fontSize: 15, fontWeight: on ? 700 : 500, fontFamily: 'inherit', textAlign: 'left',
  });
  const heading: React.CSSProperties = { color: T.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '8px 18px 4px' };
  const divider = <div style={{ height: 1, background: T.border, margin: '6px 0' }} />;

  return (
    <>
      {open && (
        <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 949, background: 'rgba(0,0,0,0.28)' }}>
          <div
            role="menu" aria-label="Mer"
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute', right: 12, left: 12, marginLeft: 'auto', maxWidth: 320,
              bottom: `calc(${BOTTOM_NAV_H + BOTTOM_GAP + 8}px + env(safe-area-inset-bottom, 0px))`,
              maxHeight: `calc(100% - ${BOTTOM_NAV_H + BOTTOM_GAP + 24}px - env(safe-area-inset-bottom, 0px) - env(safe-area-inset-top, 0px))`, overflowY: 'auto',
              ...huntBarSurface(T.surface, T.border), borderRadius: 18, padding: '6px 0', color: T.text,
            }}
          >
            {more.length > 0 && <>
              {more.map(k => (
                <button key={k} role="menuitem" onClick={() => pick(k)} style={row(active === k)}>
                  <Icon k={k as Exclude<BottomKey, 'q'>} size={20} />{LABELS[k]}
                  {!!badges?.[k] && <UnreadBadge n={badges[k]!} style={{ marginLeft: 'auto' }} />}
                </button>
              ))}
              {divider}
            </>}
            <div style={heading}>Jakt</div>
            <button role="menuitem" onClick={run(onMerJakt)} style={row(active === 'jakt')}><Icon k="jakt" size={20} />Mer jakt</button>
            <button role="menuitem" onClick={run(onErbjud)} style={row()}><Icon k="erbjud" size={20} />Erbjud jakt</button>
            <button role="menuitem" onClick={run(onViltkott)} style={row()}><Icon k="viltkott" size={20} />Viltkött</button>
            {divider}
            <div style={heading}>Mina sidor</div>
            <button role="menuitem" onClick={run(onProfil)} style={row(active === 'profil')}><Icon k="profil" size={20} />Min profil</button>
            {onAdmin && <button role="menuitem" onClick={run(onAdmin)} style={row(active === 'admin')}><Icon k="admin" size={20} />Adminsida</button>}
            <div style={{ ...row(), cursor: 'default', justifyContent: 'space-between' }}><ThemeSwitch color={T.text} /></div>
            {divider}
            <button role="menuitem" onClick={run(onLogout)} style={{ ...row(), color: T.textSub }}><Icon k="logout" size={20} />Logga ut</button>
          </div>
        </div>
      )}
      <nav
        aria-label="Huvudmeny"
        style={{
          position: 'fixed', left: 12, right: 12, bottom: `calc(${BOTTOM_GAP}px + env(safe-area-inset-bottom, 0px))`, zIndex: 950,
          maxWidth: 520, margin: '0 auto', height: BOTTOM_NAV_H, padding: '0 6px', boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', gap: 2, ...huntBarSurface(T.bg, T.border),
        }}
      >
        {primary.map(k => k === 'q' ? (
          <QButton key={k} onOpen={listen => { setOpen(false); onQ(listen); }} title="Assistent" style={item(!!qOpen)}>
            <img src="/qvenari-logo.png" alt="" style={{ height: 22, width: 'auto', objectFit: 'contain' }} />
            <span style={labelStyle}>{LABELS.q}</span>
          </QButton>
        ) : (
          <button key={k} type="button" onClick={() => pick(k)} aria-current={active === k && !qOpen ? 'page' : undefined} style={item(active === k && !open && !qOpen)}
            aria-label={badges?.[k] ? `${LABELS[k]}, ${badges[k]} olästa` : undefined}>
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <Icon k={k} />
              {!!badges?.[k] && <UnreadBadge n={badges[k]!} style={{ position: 'absolute', top: -6, left: '100%', marginLeft: -8 }} />}
            </span>
            <span style={labelStyle}>{LABELS[k]}</span>
          </button>
        ))}
        <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-haspopup="menu" style={item(merActive)}>
          <Icon k="mer" /><span style={labelStyle}>Mer</span>
        </button>
      </nav>
    </>
  );
}
